import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import API, { setAuthRefreshDisabled } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const loggedOutRef = useRef(false);

  // Load user on mount — cookie is sent automatically via withCredentials.
  // Prod smoke #13: only a real "not signed in" answer (401 / 403) means logged out. A 429
  // (rate limited), a 5xx or a network error says nothing about the session, so it is retried
  // with backoff (Retry-After honoured, capped) instead of showing the signed-out UI; after the
  // quick retries the page renders and a slow background retry picks the session up later.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const QUICK_RETRIES = 4;
    const load = (attempt) => {
      API.get('/auth/me')
        .then(r => {
          // Ignore a stale /auth/me that finishes after the user already logged out
          if (!cancelled && !loggedOutRef.current) setUser(r.data.user);
          if (!cancelled) setLoading(false);
        })
        .catch(err => {
          if (cancelled) return;
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            setLoading(false);
            return;
          }
          const retryAfter = Number.parseFloat(err?.response?.headers?.['retry-after']);
          const backoff = Math.min(1000 * 2 ** attempt, 8000);
          const delay = Number.isFinite(retryAfter) && retryAfter > 0
            ? Math.min(retryAfter * 1000, attempt < QUICK_RETRIES ? 10000 : 60000)
            : (attempt < QUICK_RETRIES ? backoff : 30000);
          if (attempt + 1 >= QUICK_RETRIES) setLoading(false);
          if (attempt < 20) timer = setTimeout(() => { if (!cancelled && !loggedOutRef.current) load(attempt + 1); }, delay);
        });
    };
    load(0);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  const login = useCallback(async (email, password) => {
    loggedOutRef.current = false;
    setAuthRefreshDisabled(false);
    const { data } = await API.post('/auth/login', { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  // Login page: POST /auth/login already set the session cookies and returned the user when no
  // second factor is needed; adopt that response instead of posting the credentials again.
  const completeLogin = useCallback((loggedInUser) => {
    loggedOutRef.current = false;
    setAuthRefreshDisabled(false);
    setUser(loggedInUser);
    return loggedInUser;
  }, []);

  const signup = useCallback(async (payload) => {
    loggedOutRef.current = false;
    setAuthRefreshDisabled(false);
    const { data } = await API.post('/auth/signup', payload);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    // Clear local session first so a single click always shows the signed-out UI,
    // even if the network call is slow or a refresh interceptor is in flight.
    loggedOutRef.current = true;
    setAuthRefreshDisabled(true);
    setUser(null);
    try {
      await API.post('/auth/logout');
    } catch {
      // Local session is already cleared
    }
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, completeLogin, signup, logout, updateUser, isLoggedIn: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
